import { useEffect, useState } from 'react'

/**
 * The app sidebar's current rendered width, so a fixed-to-viewport footer can inset itself
 * instead of rendering underneath it. Measured off the element because the sidebar collapses
 * for several independent reasons, and mirroring all of them here would duplicate width
 * constants that can silently drift. Zero when the page has no sidebar.
 */
export const SIDEBAR_ROOT_ATTRIBUTE = 'data-sidebar-root'

/**
 * The app sidebar's current rendered width, so a fixed-to-viewport footer
 * (`OnboardingStickyNav`) can inset itself instead of rendering underneath it.
 *
 * Measured off the element rather than reconstructed from the inputs that drive
 * it. The sidebar collapses itself for several independent reasons — its own
 * toggle, small viewports, the Haven panel forcing it shut — and mirroring all
 * of that out of the component means re-deriving state it already owns, and
 * duplicating its width constants on this side where they can silently drift.
 * The rendered width is the one thing that is true regardless of the reason.
 *
 * Zero when there's no sidebar on the page: a footer flush to the viewport edge
 * is a safer wrong answer than one inset by a guessed width.
 */
export const useFixedFooterOffset = (): number => {
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const sidebar = document.querySelector(`[${SIDEBAR_ROOT_ATTRIBUTE}]`)
    if (!sidebar) return undefined

    const measure = () => setOffset(sidebar.getBoundingClientRect().width)
    measure()

    // Fires through the sidebar's width transition too, so the footer tracks it
    // rather than jumping once the animation lands.
    const observer = new ResizeObserver(measure)
    observer.observe(sidebar)

    // A ResizeObserver frame can land mid-transition, leaving the offset at a
    // stale in-between width after a breakpoint change. Re-measure once the
    // sidebar's width animation actually settles.
    sidebar.addEventListener('transitionend', measure)

    return () => {
      observer.disconnect()
      sidebar.removeEventListener('transitionend', measure)
    }
  }, [])

  return offset
}
